import React, { useRef, useState } from "react";
import { api, ROLE_LABELS, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, Upload, Trash2, UserCircle2, AlertTriangle, PencilLine } from "lucide-react";
import SynapseLoader from "@/components/SynapseLoader";
import SignaturePad from "@/components/SignaturePad";
import InstallAppButton from "@/components/InstallAppButton";
import LocationsEditor from "@/components/LocationsEditor";
import AbsenceSection from "@/components/AbsenceSection";
import { isPushSupported, getPushPermission, isPushActive as checkPushActive, subscribeToPush, unsubscribeFromPush } from "@/lib/push";

export default function SettingsPage() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const isOrg = user?.profession === "laboratorio" || user?.profession === "diagnostica";
  const initialFormRef = React.useRef(null);
  const [form, setForm] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    company_name: user?.company_name || "",
    specialization: user?.specialization || "",
    academic_title: user?.academic_title || "",
    phone: user?.phone || "",
    website: user?.website || "",
    bio: user?.bio || "",
    email: user?.email || "",
    fiscal_code: user?.fiscal_code || "",
    birth_date: user?.birth_date || "",
    profession: user?.profession || "medico_specialista",
    order_province: user?.order_province || "",
    order_number: user?.order_number || "",
  });
  const [locations, setLocations] = useState(user?.locations || []);
  const [emailNotif, setEmailNotif] = useState(user?.email_notifications ?? true);
  const [waNotif, setWaNotif] = useState(user?.whatsapp_notifications ?? false);
  const [saving, setSaving] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [sigBusy, setSigBusy] = useState(false);
  const [sigDraft, setSigDraft] = useState(null);
  const [sigEditing, setSigEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [delPwd, setDelPwd] = useState("");
  const [delConfirm, setDelConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [pushStatus, setPushStatus] = useState("default"); // default|granted|denied|unsupported
  const [pushActive, setPushActive] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const fileRef = useRef();
  const logoFileRef = useRef();
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Snapshot dello stato iniziale per rilevare modifiche non salvate
  React.useEffect(() => {
    initialFormRef.current = JSON.stringify({
      form,
      locations,
      emailNotif,
      waNotif,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const currentSnapshot = JSON.stringify({ form, locations, emailNotif, waNotif });
  const isDirty = initialFormRef.current !== null && initialFormRef.current !== currentSnapshot;

  // Avviso al tentativo di lasciare la pagina con modifiche non salvate
  React.useEffect(() => {
    const beforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [isDirty]);

  React.useEffect(() => {
    (async () => {
      if (!(await isPushSupported())) { setPushStatus("unsupported"); return; }
      setPushStatus(await getPushPermission());
      setPushActive(await checkPushActive());
    })();
  }, []);

  const onTogglePush = async (on) => {
    setPushBusy(true);
    try {
      if (on) {
        await subscribeToPush();
        setPushActive(true); setPushStatus("granted");
        toast.success("Notifiche push attivate");
      } else {
        await unsubscribeFromPush();
        setPushActive(false);
        toast.success("Notifiche push disattivate");
      }
    } catch (e) {
      toast.error(e.message || "Errore push");
      setPushStatus(await getPushPermission());
    } finally { setPushBusy(false); }
  };

  const onDeleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/users/me", { data: { password: delPwd, confirm: delConfirm.trim().toUpperCase() } });
      toast.success("Account eliminato");
      if (logout) await logout();
      navigate("/login", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally { setDeleting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleanLocations = (locations || [])
        .map((l) => ({ id: l.id, name: (l.name || "").trim(), city: (l.city || "").trim(), address: (l.address || "").trim() }))
        .filter((l) => l.name && l.city);
      await api.patch("/users/me", {
        ...form,
        locations: cleanLocations,
        email_notifications: emailNotif,
        whatsapp_notifications: waNotif,
      });
      await refreshUser();
      // Reset snapshot: le modifiche sono ora persistite
      initialFormRef.current = JSON.stringify({ form, locations, emailNotif, waNotif });
      toast.success("Impostazioni salvate correttamente");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("File troppo grande (max 5 MB)"); return; }
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/users/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshUser();
      toast.success("Immagine profilo aggiornata");
    } catch (er) {
      toast.error(formatApiErrorDetail(er.response?.data?.detail) || "Errore upload");
    } finally { setAvatarBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeAvatar = async () => {
    setAvatarBusy(true);
    try {
      await api.delete("/users/me/avatar");
      await refreshUser();
      toast.success("Immagine rimossa");
    } catch (er) {
      toast.error("Errore");
    } finally { setAvatarBusy(false); }
  };

  const onLogoFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("File troppo grande (max 5 MB)"); return; }
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/users/me/studio-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshUser();
      toast.success("Logo studio aggiornato");
    } catch (er) {
      toast.error(formatApiErrorDetail(er.response?.data?.detail) || "Errore upload");
    } finally { setLogoBusy(false); if (logoFileRef.current) logoFileRef.current.value = ""; }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      await api.delete("/users/me/studio-logo");
      await refreshUser();
      toast.success("Logo rimosso");
    } catch (er) { toast.error("Errore"); }
    finally { setLogoBusy(false); }
  };

  const saveSignature = async () => {
    if (!sigDraft) { toast.error("Disegna prima la firma."); return; }
    setSigBusy(true);
    try {
      await api.post("/users/me/signature", { signature_b64: sigDraft });
      await refreshUser();
      setSigEditing(false); setSigDraft(null);
      toast.success("Firma salvata nel profilo");
    } catch (er) {
      toast.error(formatApiErrorDetail(er.response?.data?.detail) || "Errore salvataggio firma");
    } finally { setSigBusy(false); }
  };

  const removeSignature = async () => {
    setSigBusy(true);
    try {
      await api.delete("/users/me/signature");
      await refreshUser();
      setSigDraft(null); setSigEditing(false);
      toast.success("Firma rimossa");
    } catch (er) { toast.error("Errore"); }
    finally { setSigBusy(false); }
  };

  const displayName = (isOrg ? user?.company_name : "") || `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.email;
  const initials = (displayName || "?").split(" ").slice(0, 2).map(s => s[0]).join("").toUpperCase();

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto pb-32">
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#1E3A8A]/80">Account</p>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-slate-900">Impostazioni</h1>
      </div>

      {/* Avatar */}
      <Card className="p-6 bg-white border-slate-200 rounded-2xl shadow-sm shadow-black/5">
        <h2 className="font-heading text-lg font-bold text-slate-900 mb-4">Immagine profilo</h2>
        <div className="flex items-center gap-6">
          <div className="h-24 w-24 rounded-full overflow-hidden ring-2 ring-white/10 bg-gradient-to-br from-[#1E3A8A] to-[#10B981] flex items-center justify-center text-white font-heading font-extrabold text-3xl flex-shrink-0"
               data-testid="settings-avatar">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="avatar" className="h-full w-full object-cover" />
            ) : (initials || <UserCircle2 className="h-12 w-12" />)}
          </div>
          <div className="flex-1">
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile}
                   className="hidden" data-testid="avatar-file-input" />
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}
                      disabled={avatarBusy} data-testid="avatar-upload-btn"
                      className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900">
                {avatarBusy ? <SynapseLoader size="sm" inline className="mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                {avatarBusy ? "Caricamento…" : "Carica foto"}
              </Button>
              {user?.avatar_url && (
                <Button type="button" variant="ghost" onClick={removeAvatar}
                        disabled={avatarBusy} data-testid="avatar-remove-btn"
                        className="text-red-600 hover:bg-red-50 hover:text-red-700">
                  <Trash2 className="h-4 w-4 mr-2" /> Rimuovi
                </Button>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              JPG, PNG o WEBP. Max 5 MB. L'immagine verrà ritagliata a un quadrato 256×256.
            </p>
          </div>
        </div>
      </Card>

      {/* Logo studio (solo per medici specialisti) */}
      {user?.profession === "medico_specialista" && (
        <Card className="p-6 bg-white border-slate-200 rounded-2xl shadow-sm shadow-black/5 mt-4">
          <h2 className="font-heading text-lg font-bold text-slate-900 mb-1">Logo dello studio (carta intestata)</h2>
          <p className="text-xs text-slate-500 mb-4">
            Se carichi un logo, sarà usato come <strong>carta intestata dei referti PDF</strong> al posto del solo nome (il tuo nome resta sotto in caratteri più piccoli).
          </p>
          <div className="flex items-center gap-6">
            <div className="h-24 w-40 rounded-lg overflow-hidden bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0"
                 data-testid="settings-studio-logo">
              {user?.studio_logo_url ? (
                <img src={user.studio_logo_url} alt="Logo studio" className="max-h-full max-w-full object-contain" />
              ) : (
                <span className="text-xs text-slate-400 italic">Nessun logo</span>
              )}
            </div>
            <div className="flex-1">
              <input ref={logoFileRef} type="file" accept="image/jpeg,image/png,image/webp"
                     onChange={onLogoFile} className="hidden" data-testid="studio-logo-input" />
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={() => logoFileRef.current?.click()}
                        disabled={logoBusy} data-testid="studio-logo-upload"
                        className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900">
                  {logoBusy ? <SynapseLoader size="sm" inline className="mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                  {logoBusy ? "Caricamento…" : (user?.studio_logo_url ? "Sostituisci logo" : "Carica logo")}
                </Button>
                {user?.studio_logo_url && (
                  <Button type="button" variant="ghost" onClick={removeLogo}
                          disabled={logoBusy} data-testid="studio-logo-remove"
                          className="text-red-600 hover:bg-red-50 hover:text-red-700">
                    <Trash2 className="h-4 w-4 mr-2" /> Rimuovi
                  </Button>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                JPG, PNG (con trasparenza) o WEBP. Max 5 MB. Ridimensionato a max 800×300px mantenendo le proporzioni.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Firma salvata in profilo (solo per medici specialisti) */}
      {user?.profession === "medico_specialista" && (
        <Card className="p-6 bg-white border-slate-200 rounded-2xl shadow-sm shadow-black/5 mt-4">
          <h2 className="font-heading text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
            <PencilLine className="h-5 w-5 text-emerald-600" /> Firma digitale
          </h2>
          <p className="text-xs text-slate-500 mb-4">
            Salva la tua firma una volta sola: verrà usata automaticamente su ogni referto PDF che emetterai.
            Puoi comunque ridisegnarla sul singolo referto quando serve.
          </p>

          {user?.signature_url && !sigEditing ? (
            <div className="flex items-center gap-4 flex-wrap">
              <div className="h-24 w-56 rounded-lg overflow-hidden bg-white border border-slate-200 flex items-center justify-center flex-shrink-0"
                   data-testid="settings-saved-signature">
                <img src={user.signature_url} alt="Firma" className="max-h-full max-w-full object-contain" />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={() => { setSigEditing(true); setSigDraft(null); }}
                        disabled={sigBusy} data-testid="signature-redraw"
                        className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
                  <PencilLine className="h-4 w-4 mr-2" /> Ridisegna
                </Button>
                <Button type="button" variant="ghost" onClick={removeSignature} disabled={sigBusy}
                        data-testid="signature-remove"
                        className="text-red-600 hover:bg-red-50 hover:text-red-700">
                  <Trash2 className="h-4 w-4 mr-2" /> Rimuovi
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <SignaturePad value={sigDraft} onChange={setSigDraft} height={140} />
              <div className="mt-3 flex flex-wrap gap-2 justify-end">
                {user?.signature_url && (
                  <Button type="button" variant="outline" onClick={() => { setSigEditing(false); setSigDraft(null); }}
                          disabled={sigBusy}
                          className="border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
                    Annulla
                  </Button>
                )}
                <Button type="button" onClick={saveSignature} disabled={sigBusy || !sigDraft}
                        data-testid="signature-save"
                        className="h-10 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-700
                                   hover:from-emerald-700 hover:to-teal-800 text-white font-semibold shadow-md
                                   disabled:opacity-50">
                  {sigBusy ? <><SynapseLoader size="sm" inline className="mr-2" /> Salvo…</>
                            : <><ShieldCheck className="h-4 w-4 mr-2" /> Salva firma nel profilo</>}
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Profile */}
      <Card className="p-6 bg-white border-slate-200 rounded-2xl shadow-sm shadow-black/5 mt-4">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="font-heading text-lg font-bold text-slate-900 mb-1">Profilo professionale</h2>
            <p className="text-xs text-slate-500">
              Ruolo: <span className="font-semibold text-slate-800">{ROLE_LABELS[user?.profession] || ROLE_LABELS[user?.role]}</span>
              <span className="ml-2">Email: <span className="font-mono text-slate-800">{user?.email}</span></span>
            </p>
          </div>
          {user?.verification_status === "verified" && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-400/25">
              <ShieldCheck className="h-3.5 w-3.5" /> Verificato
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 [&_label]:text-slate-700 [&_label]:font-medium [&_input]:bg-white [&_input]:border-slate-300 [&_input]:text-slate-900 [&_input]:placeholder:text-slate-400 [&_textarea]:bg-white [&_textarea]:border-slate-300 [&_textarea]:text-slate-900 [&_textarea]:placeholder:text-slate-400 [&_button[role=combobox]]:bg-white [&_button[role=combobox]]:border-slate-300 [&_button[role=combobox]]:text-slate-900">
          {isOrg && (
            <div className="md:col-span-2">
              <Label>Ragione Sociale</Label>
              <Input value={form.company_name} onChange={upd("company_name")}
                     placeholder="Denominazione ufficiale della struttura"
                     data-testid="settings-company-name" />
              <p className="text-[11px] text-slate-500 mt-1">
                Nome mostrato ai colleghi nella rubrica e nelle comunicazioni.
              </p>
            </div>
          )}
          <div><Label>{isOrg ? "Referente — Nome" : "Nome"}</Label><Input value={form.first_name} onChange={upd("first_name")} data-testid="settings-first-name" /></div>
          <div><Label>{isOrg ? "Referente — Cognome" : "Cognome"}</Label><Input value={form.last_name} onChange={upd("last_name")} data-testid="settings-last-name" /></div>
          <div>
            <Label>Telefono</Label>
            <Input value={form.phone} onChange={upd("phone")} data-testid="settings-phone" placeholder="+39 …" />
            <p className="text-[11px] text-slate-500 mt-1">Mostrato in rubrica ai colleghi verificati.</p>
          </div>
          <div><Label>{isOrg ? "Ambito di attività" : "Specializzazione"}</Label><Input value={form.specialization} onChange={upd("specialization")} data-testid="settings-specialty" placeholder={isOrg ? "Es. Analisi cliniche, microbiologia" : ""} /></div>
          <div className="md:col-span-2">
            <Label>Sito web <span className="text-slate-400 text-xs">(facoltativo)</span></Label>
            <Input value={form.website} onChange={upd("website")} data-testid="settings-website"
                   placeholder="Es. https://www.tuostudio.it" maxLength={200} />
            <p className="text-[11px] text-slate-500 mt-1">
              Mostrato in rubrica come link cliccabile.
            </p>
          </div>
          <div className="md:col-span-2"><Label>Titolo di studio / qualifica <span className="text-slate-400 text-xs">(facoltativo)</span></Label>
            <Input value={form.academic_title} onChange={upd("academic_title")}
                   placeholder="Es. Specialista in Cardiologia · Ph.D. Medicina Interna · Prof. Associato"
                   maxLength={120}
                   data-testid="settings-academic-title" />
            <p className="text-[11px] text-slate-500 mt-1">
              Mostrato accanto al tuo nome in rubrica per far riconoscere subito le tue qualifiche.
            </p>
          </div>
          <div className="md:col-span-2">
            <Label>Descrizione professionale</Label>
            <Textarea rows={4} value={form.bio} onChange={upd("bio")}
                      placeholder="Presentati ai colleghi: ambiti di competenza, esperienza, patologie di riferimento, orari studio…"
                      data-testid="settings-bio" maxLength={800} />
            <div className="text-[11px] text-slate-500 mt-1 flex justify-between">
              <span>Visibile agli altri professionisti verificati. Ricercabile nella rubrica.</span>
              <span>{(form.bio || "").length}/800</span>
            </div>
          </div>
        </div>
        <div className="mt-6 pt-4 border-t border-slate-100">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Documenti e Ordine</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Email di accesso</Label>
              <Input type="email" value={form.email} onChange={upd("email")}
                     data-testid="settings-email" placeholder="nome@dominio.it" />
              <p className="text-[11px] text-amber-600 mt-1">
                ⚠ Modificando l'email dovrai usarla per accedere al prossimo login.
              </p>
            </div>
            <div>
              <Label>Codice Fiscale / P. IVA</Label>
              <Input value={form.fiscal_code}
                     onChange={(e) => setForm({ ...form, fiscal_code: e.target.value.toUpperCase() })}
                     data-testid="settings-fiscal-code"
                     placeholder="RSSMRA85M01H501Z" maxLength={32} />
            </div>
            <div>
              <Label>Data di nascita</Label>
              <Input type="date" value={form.birth_date} onChange={upd("birth_date")}
                     data-testid="settings-birth-date" />
            </div>
            <div>
              <Label>Ruolo / Professione</Label>
              <Select value={form.profession} onValueChange={(v) => setForm({ ...form, profession: v })}>
                <SelectTrigger data-testid="settings-profession"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="medico_medicina_generale">Medico di Medicina Generale</SelectItem>
                  <SelectItem value="medico_specialista">Medico Specialista</SelectItem>
                  <SelectItem value="laboratorio">Laboratorio</SelectItem>
                  <SelectItem value="diagnostica">Struttura diagnostica</SelectItem>
                  <SelectItem value="altro_professionista">Altro professionista</SelectItem>
                </SelectContent>
              </Select>
              {form.profession !== user?.profession && (
                <p className="text-[11px] text-amber-600 mt-1">
                  ⚠ Cambiando ruolo, il tuo account tornerà in stato "In verifica" per il controllo dell'Ordine.
                </p>
              )}
            </div>
            <div>
              <Label>Ordine / Provincia</Label>
              <Input value={form.order_province} onChange={upd("order_province")}
                     placeholder="Es. Roma" data-testid="settings-order-province" />
            </div>
            <div>
              <Label>Numero di iscrizione</Label>
              <Input value={form.order_number} onChange={upd("order_number")}
                     placeholder="Es. 12345" data-testid="settings-order-number" />
            </div>
          </div>
        </div>
        <div className="mt-4 text-xs text-slate-500 border-t border-slate-100 pt-3">
          Tutti i campi sono modificabili. Alcune modifiche (email, ruolo, codice fiscale) potrebbero richiedere una nuova verifica da parte dell'amministratore.
        </div>
      </Card>

      {/* Sedi di lavoro */}
      <Card className="p-6 bg-white border-slate-200 rounded-2xl shadow-sm shadow-black/5 mt-4">
        <h2 className="font-heading text-lg font-bold text-slate-900 mb-1">Sede / sedi di lavoro</h2>
        <p className="text-xs text-slate-500 mb-4">
          I colleghi ti troveranno anche filtrando per città. Se visiti in più strutture (poliambulatori, sedi distaccate), spunta <em>più sedi</em>.
        </p>
        <LocationsEditor value={locations} onChange={setLocations} testidPrefix="settings-loc" />
      </Card>

      {/* Assenza / Sostituzione (MMG in ferie) */}
      <Card className="p-6 bg-white border-slate-200 rounded-2xl shadow-sm shadow-black/5 mt-4">
        <h2 className="font-heading text-lg font-bold text-slate-900 mb-1">Assenza / Sostituzione</h2>
        <p className="text-xs text-slate-500 mb-4">
          Prima di andare in ferie, delega la gestione delle nuove richieste a un collega MMG. Al rientro tutto torna sotto il tuo controllo.
        </p>
        <AbsenceSection user={user} />
      </Card>

      {/* Notifications */}
      <Card className="p-6 bg-white border-slate-200 rounded-2xl shadow-sm shadow-black/5 mt-4">
        <h2 className="font-heading text-lg font-bold text-slate-900 mb-2">Installa l'app sul telefono</h2>
        <p className="text-xs text-slate-500 mb-4">
          Aggiungi Synappsi alla Home del tuo dispositivo per aprirla come un'app nativa e ricevere le notifiche urgenti anche ad app chiusa.
        </p>
        <InstallAppButton variant="cta" />
        <p className="text-[11px] text-slate-400 mt-3">
          Il pulsante appare solo se l'app non è già installata. Su iPhone/iPad segui la guida "Aggiungi a Home".
        </p>
      </Card>

      {/* Notifications */}
      <Card className="p-6 bg-white border-slate-200 rounded-2xl shadow-sm shadow-black/5 mt-4">
        <h2 className="font-heading text-lg font-bold text-slate-900 mb-4">Notifiche</h2>
        <div className="flex items-center justify-between py-2 border-b border-slate-100">
          <div>
            <div className="font-medium text-slate-900">Email</div>
            <div className="text-xs text-slate-500">Ricevi email per ogni nuova comunicazione.</div>
          </div>
          <Switch checked={emailNotif} onCheckedChange={setEmailNotif} data-testid="settings-email-notif" />
        </div>
        <div className="flex items-center justify-between py-2 mt-2 border-b border-slate-100">
          <div>
            <div className="font-medium text-slate-900">Push (notifiche urgenti sul telefono)</div>
            <div className="text-xs text-slate-500">
              {pushStatus === "granted" ? "Ricevi una notifica anche a app chiusa per richieste alta/critica priorità."
                : pushStatus === "denied" ? "Notifiche bloccate nel browser. Sblocca dalle impostazioni del browser."
                : pushStatus === "unsupported" ? "Il browser non supporta le notifiche push."
                : "Attivale per essere avvisato subito per richieste urgenti/critiche."}
            </div>
          </div>
          <Switch checked={pushActive} onCheckedChange={onTogglePush}
                  disabled={pushBusy || pushStatus === "unsupported" || pushStatus === "denied"}
                  data-testid="settings-push-notif" />
        </div>
        <div className="flex items-center justify-between py-2 mt-2">
          <div>
            <div className="font-medium text-slate-900">WhatsApp</div>
            <div className="text-xs text-slate-500">In arrivo: richiede Twilio.</div>
          </div>
          <Switch checked={waNotif} onCheckedChange={setWaNotif} data-testid="settings-wa-notif" />
        </div>
      </Card>

      {/* Barra Salva — sticky in fondo alla pagina, sempre visibile mentre si modifica */}
      <div className="mt-6 sticky bottom-4 z-30" data-testid="settings-save-bar">
        <div className={`flex flex-col sm:flex-row items-center justify-between gap-3 rounded-2xl border px-4 py-3 shadow-lg backdrop-blur-xl transition-colors ${
          isDirty ? "bg-amber-50/95 border-amber-300" : "bg-white/95 border-slate-200"
        }`}>
          <div className="text-sm text-slate-700 flex items-center gap-2">
            {isDirty ? (
              <>
                <span className="inline-flex h-2.5 w-2.5 rounded-full bg-amber-500 animate-pulse" />
                <strong className="text-amber-800">Modifiche non salvate</strong>
                <span className="hidden sm:inline text-slate-500">— clicca <em>Salva modifiche</em> per confermare.</span>
              </>
            ) : (
              <>
                <span className="inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                <span className="text-slate-600">Tutte le modifiche sono salvate.</span>
              </>
            )}
          </div>
          <Button onClick={save} disabled={saving || !isDirty} data-testid="settings-save"
                  className="h-11 px-6 rounded-xl bg-gradient-to-r from-[#1E3A8A] via-[#2563EB] to-[#10B981]
                             hover:from-[#1E40AF] hover:via-[#3B82F6] hover:to-[#059669]
                             disabled:opacity-50 disabled:cursor-not-allowed
                             text-white font-semibold shadow-lg shadow-emerald-500/20 w-full sm:w-auto">
            {saving ? <><SynapseLoader size="sm" inline className="mr-2" /> Salvataggio…</> : "Salva modifiche"}
          </Button>
        </div>
      </div>
      {/* Elimina account */}
      <Card className="p-5 bg-white border-red-400/20 rounded-2xl shadow-sm shadow-black/5 mt-4" data-testid="settings-danger-zone">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="text-sm text-slate-700">
            <div className="font-semibold text-slate-900">Elimina account</div>
            <div className="text-xs text-slate-500 mt-1">
              I dati personali vengono cancellati (GDPR art. 17). Le conversazioni cliniche rimangono in forma anonima per continuità clinica. Operazione irreversibile.
            </div>
          </div>
          <Button variant="outline" onClick={() => setDeleteOpen(true)}
                  data-testid="settings-delete-account-btn"
                  className="border-red-300 bg-red-50 text-red-700 hover:bg-red-100 hover:text-red-800">
            Elimina account
          </Button>
        </div>
      </Card>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" /> Elimina account
            </DialogTitle>
            <DialogDescription>
              Operazione irreversibile. Il tuo account e i tuoi dati personali verranno cancellati (GDPR art. 17). Per confermare inserisci la password e digita <strong>ELIMINA</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Password attuale</Label>
              <Input type="password" value={delPwd} onChange={(e) => setDelPwd(e.target.value)}
                     data-testid="delete-password" autoComplete="current-password" />
            </div>
            <div>
              <Label>Scrivi <span className="font-mono font-bold text-red-700">ELIMINA</span> per confermare</Label>
              <Input value={delConfirm} onChange={(e) => setDelConfirm(e.target.value)}
                     data-testid="delete-confirm" placeholder="ELIMINA" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} data-testid="delete-cancel">
              Annulla
            </Button>
            <Button disabled={deleting || !delPwd || delConfirm.trim().toUpperCase() !== "ELIMINA"}
                    onClick={onDeleteAccount}
                    data-testid="delete-confirm-btn"
                    className="bg-red-600 hover:bg-red-700 text-white">
              {deleting ? <><SynapseLoader size="sm" inline className="mr-2" /> Eliminazione…</> : "Elimina definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
